import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUser,
  FiMail,
  FiPhone,
  FiMapPin,
  FiCalendar,
  FiEdit3,
  FiSave,
  FiX,
  FiCamera,
  FiEye,
  FiEyeOff,
  FiShield,
  FiSettings,
  FiAward,
  FiTrendingUp,
  FiBookOpen,
  FiClock,
  FiTarget,
  FiStar,
  FiAlertCircle,
  FiGlobe,
  FiBell,
  FiLock,
  FiActivity,
  FiDownload,
  FiTrash2,
  FiGift,
  FiCopy,
  FiLink,
  FiUsers,
  FiDollarSign
} from 'react-icons/fi';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { useNotification } from '../contexts/NotificationContext';
import { userAPI, analyticsAPI, referralAPI } from '../services/api';

const inputClass = 'w-full px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400 disabled:bg-slate-50 disabled:text-slate-500';
const labelClass = 'block text-sm font-medium text-slate-700 mb-1';

const Profile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [formData, setFormData] = useState({});
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [preferences, setPreferences] = useState({
    notifications: {
      email: true,
      push: true,
      testReminders: true,
      progressUpdates: false
    },
    privacy: {
      profileVisibility: 'public',
      showProgress: true,
      showAchievements: true
    },
    display: {
      theme: 'light',
      language: 'en',
      timezone: 'UTC'
    }
  });

  // Referral state
  const [referralCode, setReferralCode] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [referralQR, setReferralQR] = useState('');
  const [referralLoading, setReferralLoading] = useState(false);
  const [referralError, setReferralError] = useState(null);
  const [generatingReferral, setGeneratingReferral] = useState(false);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    totalEarnings: 0,
    pendingRewards: 0,
    completedRewards: 0,
    subscriptionCommissions: { totalAmount: 0, totalCommissions: 0 }
  });
  const [teaching, setTeaching] = useState({ subjects: [], rates: {} });
  const subjectOptions = [
    'English', 'Mathematics', 'Physics', 'Chemistry', 'Biology'
  ];
  
  const { showSuccess, showError } = useNotification();
  const navigate = useNavigate();
  
  const loadProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await userAPI.getProfile();
      
      if (response.data.success) {
        const userData = response.data.data.user;
        const displayName = userData.fullName || [userData.firstName, userData.lastName].filter(Boolean).join(' ').trim() || 'User';
        setProfile({ ...userData, fullName: userData.fullName || displayName });
        setFormData({
          fullName: displayName,
          email: userData.email || '',
          phone: userData.phoneNumber || userData.phone || '',
          dateOfBirth: userData.dateOfBirth ? userData.dateOfBirth.split('T')[0] : '',
          address: userData.address || '',
          bio: userData.bio || ''
        });

        // Load preferences/settings if present on the user
        if (userData.settings) {
          setPreferences(prev => ({
            ...prev,
            // Map backend settings to UI preferences where possible
            notifications: {
              ...prev.notifications,
              email: userData.settings.notifications?.email ?? prev.notifications.email,
              push: userData.settings.notifications?.push ?? prev.notifications.push
            },
            privacy: {
              ...prev.privacy,
              // Convert boolean showProfile into visibility setting
              profileVisibility: userData.settings.privacy?.showProfile === false ? 'private' : 'public',
              showProgress: userData.settings.privacy?.showProgress ?? prev.privacy.showProgress
            },
            display: {
              ...prev.display,
              // Infer theme from accessibility.darkMode
              theme: userData.settings.accessibility?.darkMode ? 'dark' : prev.display.theme
            }
          }));
        }

        if (userData.teaching) {
          setTeaching({
            subjects: Array.isArray(userData.teaching.subjects) ? userData.teaching.subjects : [],
            rates: userData.teaching.rates || {}
          });
        }

        // Load user stats if available
        try {
          const statsResponse = await userAPI.getUserStats(userData._id);
          if (statsResponse.data.success) {
            setProfile(prev => ({
              ...prev,
              stats: statsResponse.data.data
            }));
          }
        } catch (statsError) {
          console.warn('Stats not available:', statsError);
          // Use mock stats as fallback
          setProfile(prev => ({
            ...prev,
            stats: {
              testsCompleted: 0,
              averageScore: 0,
              studyHours: 0,
              rank: 'N/A',
              totalPoints: 0,
              achievements: 0,
              completedThisWeek: 0,
              weeklyGoal: 5,
              weeklyProgress: {
                testsCompleted: 0,
                averageScore: 0,
                studyTime: 0,
                goal: 5,
                progress: 0
              },
              subjectPerformance: {},
              recentActivity: []
            }
          }));
        }

        // Load performance analytics with error handling
        try {
          const performanceResponse = await analyticsAPI.getPerformanceAnalytics({
            timeRange: '30d',
            subject: 'all',
            grade: 'all',
            testType: 'all'
          });
          
          if (performanceResponse.data.success) {
            setProfile(prev => ({
              ...prev,
              performance: performanceResponse.data.data
            }));
          }
        } catch (performanceError) {
          console.warn('Performance analytics not available:', performanceError);
          // Continue without performance data
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      showError('Failed to load profile data');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  // Load referral code, URL, and QR
  const loadReferral = useCallback(async () => {
    try {
      setReferralLoading(true);
      setReferralError(null);

      // Attempt to get my stats (referral code)
      try {
        const statsRes = await referralAPI.getMyStats();
        if (statsRes.data?.success) {
          const data = statsRes.data.data || {};
          setReferralCode(data?.referralCode || '');

          const rewards = Array.isArray(data.rewards) ? data.rewards : [];
          const pendingRewards = rewards.filter(r => r.status === 'pending').length;
          const completedRewards = rewards.filter(r => r.status === 'sent' || r.status === 'completed').length;

          setReferralStats({
            totalReferrals: data.totalReferrals || 0,
            totalEarnings: data.totalEarnings || 0,
            pendingRewards,
            completedRewards,
            subscriptionCommissions: {
              totalAmount: data.subscriptionCommissions?.totalAmount || 0,
              totalCommissions: data.subscriptionCommissions?.totalCommissions || 0
            }
          });
        }
      } catch (err) {
        // Not fatal; may be missing permissions or code not set
        console.warn('Referral stats not available:', err);
      }

      // Get QR code and referral URL if code exists
      try {
        const qrRes = await referralAPI.getQRCode();
        if (qrRes.data?.success) {
          const { qrCode, referralUrl, referralCode } = qrRes.data.data || {};
          if (referralCode) setReferralCode(referralCode);
          if (referralUrl) setReferralUrl(referralUrl);
          if (qrCode) setReferralQR(qrCode);
        } else if (qrRes.data?.message) {
          setReferralError(qrRes.data.message);
        }
      } catch (err) {
        // If no code yet, QR endpoint returns 404
        console.warn('Referral QR not available:', err);
      }
    } catch (error) {
      console.error('Error loading referral info:', error);
      setReferralError('Failed to load referral info');
    } finally {
      setReferralLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfile();
    loadReferral();
  }, [loadProfile, loadReferral]);

  const formatCurrency = (amount) => {
    const n = Number(amount || 0);
    try {
      return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', maximumFractionDigits: 0 }).format(n);
    } catch (e) {
      return `₦${n.toLocaleString()}`;
    }
  };

  const copyToClipboard = async (text, label = 'Text') => {
    try {
      if (!text) return;
      await navigator.clipboard.writeText(text);
      showSuccess(`${label} copied to clipboard`);
    } catch (err) {
      console.error('Copy failed:', err);
      showError('Failed to copy');
    }
  };

  const downloadQRCode = () => {
    try {
      if (!referralQR) return;
      const link = document.createElement('a');
      link.href = referralQR;
      link.download = 'passqik-referral-qr.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      showSuccess('QR code downloaded');
    } catch (err) {
      console.error('Download QR failed:', err);
      showError('Failed to download QR code');
    }
  };

  const handleGenerateReferralCode = async () => {
    try {
      setGeneratingReferral(true);
      const res = await referralAPI.generateUserCode();
      if (res.data?.success) {
        const code = res.data.data?.referralCode;
        setReferralCode(code || '');
        showSuccess('Referral code generated!');
        // Refresh QR and URL
        await loadReferral();
      } else {
        showError(res.data?.message || 'Failed to generate referral code');
      }
    } catch (error) {
      console.error('Generate code failed:', error);
      showError('Failed to generate referral code');
    } finally {
      setGeneratingReferral(false);
    }
  };
  
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };
  
  const handlePasswordChange = (field, value) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePreferenceChange = (section, field, value) => {
    setPreferences(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };
  
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { // 5MB limit
        showError('File size must be less than 5MB');
        return;
      }
      
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setAvatarPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      
      // Validate form data
      if (!formData.fullName?.trim()) {
        showError('Full name is required');
        return;
      }
      
      if (!formData.email?.trim()) {
        showError('Email is required');
        return;
      }
      
      // Upload avatar if changed
      let avatarUrl = profile.avatar;
      if (avatarFile) {
        const formDataUpload = new FormData();
        formDataUpload.append('avatar', avatarFile);
        
        try {
          const uploadResponse = await userAPI.uploadAvatar(formDataUpload);
          avatarUrl = uploadResponse.data?.data?.avatar || uploadResponse.data?.avatar || uploadResponse.data?.url || avatarUrl;
        } catch (uploadError) {
          console.error('Avatar upload failed:', uploadError);
          showError('Failed to upload avatar');
          return;
        }
      }
      
      // Update profile
      const updateData = {
        ...formData,
        avatar: avatarUrl
      };
      
      const response = await userAPI.updateProfile(updateData);
      const updatedProfile = response.data?.data?.user || response.data?.user || response.data || response;
      
      setProfile(updatedProfile);
      // Keep form in sync with saved values
      setFormData({
        fullName: updatedProfile.fullName || '',
        email: updatedProfile.email || '',
        phone: updatedProfile.phoneNumber || updatedProfile.phone || '',
        dateOfBirth: updatedProfile.dateOfBirth ? updatedProfile.dateOfBirth.split('T')[0] : '',
        address: updatedProfile.address || '',
        bio: updatedProfile.bio || ''
      });
      setEditing(false);
      setAvatarFile(null);
      setAvatarPreview(null);
      
      showSuccess('Profile updated successfully!');
    } catch (error) {
      console.error('Failed to update profile:', error);
      showError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleChangePassword = async () => {
    try {
      if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
        showError('All password fields are required');
        return;
      }
      
      if (passwordData.newPassword !== passwordData.confirmPassword) {
        showError('New passwords do not match');
        return;
      }
      
      if (passwordData.newPassword.length < 8) {
        showError('New password must be at least 8 characters long');
        return;
      }
      
      await userAPI.changePassword(passwordData);
      
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      showSuccess('Password changed successfully!');
    } catch (error) {
      console.error('Failed to change password:', error);
      const msg = error.response?.data?.message || 'Failed to change password. Please check your current password.';
      showError(msg);
    }
  };

  const handleSavePreferences = async () => {
    try {
      // Map UI preferences to backend settings shape
      const mappedSettings = {
        notifications: {
          email: !!preferences.notifications?.email,
          push: !!preferences.notifications?.push,
          sms: false
        },
        privacy: {
          showProfile: preferences.privacy?.profileVisibility !== 'private',
          showProgress: !!preferences.privacy?.showProgress
        },
        accessibility: {
          fontSize: 'medium',
          darkMode: preferences.display?.theme === 'dark',
          highContrast: false
        }
      };
      await userAPI.updatePreferences(mappedSettings);
      // Update local profile settings for immediate UI feedback
      setProfile(prev => ({ ...prev, settings: mappedSettings }));
      showSuccess('Preferences saved successfully!');
    } catch (error) {
      console.error('Failed to save preferences:', error);
      const msg = error.response?.data?.message || 'Failed to save preferences. Please try again.';
      showError(msg);
    }
  };

  const handleSaveTeaching = async () => {
    try {
      setSaving(true);
      const response = await userAPI.updateProfile({ teaching });
      const updatedProfile = response.data?.data?.user || response.data?.user || response.data || response;
      setProfile(updatedProfile);
      setTeaching({
        subjects: Array.isArray(updatedProfile.teaching?.subjects) ? updatedProfile.teaching.subjects : teaching.subjects,
        rates: updatedProfile.teaching?.rates || teaching.rates
      });
      showSuccess('Teaching settings updated successfully!');
    } catch (error) {
      console.error('Failed to update teaching settings:', error);
      showError('Failed to update teaching settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const toggleSubject = (subject, checked) => {
    setTeaching(prev => {
      const subjects = checked ? Array.from(new Set([...(prev.subjects || []), subject])) : (prev.subjects || []).filter(s => s !== subject);
      const rates = checked ? prev.rates || {} : Object.fromEntries(Object.entries(prev.rates || {}).filter(([s]) => s !== subject));
      return { subjects, rates };
    });
  };

  const handleDeleteAccount = async () => {
    if (window.confirm('Are you sure you want to delete your account? This action cannot be undone.')) {
      try {
        await userAPI.deleteAccount();
        showSuccess('Account deleted successfully');
        navigate('/login');
      } catch (error) {
        console.error('Failed to delete account:', error);
        showError('Failed to delete account. Please try again.');
      }
    }
  };

  const handleExportData = async () => {
    try {
      const response = await userAPI.exportData();
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `passqik-profile-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      showSuccess('Profile data exported successfully!');
    } catch (error) {
      console.error('Failed to export data:', error);
      showError('Failed to export data. Please try again.');
    }
  };
  
  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };
  
  const getAchievementBadgeColor = (rarity) => {
    const colors = {
      common: 'secondary',
      rare: 'primary',
      epic: 'warning',
      legendary: 'success'
    };
    return colors[rarity] || 'primary';
  };
  
  const getDisplayName = () => profile?.fullName || [profile?.firstName, profile?.lastName].filter(Boolean).join(' ').trim() || 'User';
  const getInitials = (name) => {
    return (name || getDisplayName())
      .split(' ')
      .map(n => n[0])
      .filter(Boolean)
      .join('')
      .toUpperCase() || 'U';
  };

  // Derive achievements from stats when profile.achievements is empty (for all roles)
  const effectiveAchievements = profile?.achievements?.length
    ? profile.achievements
    : (() => {
        const list = [];
        const stats = profile?.stats || {};
        const tests = stats.testsCompleted || 0;
        const avg = stats.averageScore || 0;
        if (tests >= 1) list.push({ title: 'First test completed', description: 'You completed your first test.', rarity: 'common', earnedAt: new Date().toISOString() });
        if (tests >= 5) list.push({ title: '5 tests completed', description: 'You completed 5 tests.', rarity: 'rare', earnedAt: new Date().toISOString() });
        if (avg >= 80) list.push({ title: '80% or above', description: 'You achieved an average score of 80% or higher.', rarity: 'epic', earnedAt: new Date().toISOString() });
        return list;
      })();

  // Activity: connected to backend — prefer stats.recentActivity (from GET /users/:id/stats), then profile.recentActivity, then performance
  const rawActivity = (profile?.stats?.recentActivity?.length && profile.stats.recentActivity) ||
    (profile?.recentActivity?.length && profile.recentActivity) ||
    (profile?.performance?.recentTests?.length && profile.performance.recentTests.map(t => ({
      title: t.title || 'Test completed',
      description: `Score: ${t.scorePercentage ?? t.percentage ?? 0}%`,
      timestamp: t.completedAt || t.date
    }))) ||
    [];
  const effectiveActivity = rawActivity.map((a) => ({
    title: a.title || 'Activity',
    description: a.description ?? '',
    timestamp: a.timestamp || a.date
  }));

  const getProgressPercentage = () => {
    if (!profile?.stats) return 0;
    return Math.min((profile.stats.completedThisWeek / profile.stats.weeklyGoal) * 100, 100);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const tabBtn = (tab, icon, label) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${activeTab === tab ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
    >
      {icon} {label}
    </button>
  );
  
  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Profile</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage your account and preferences.</p>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-start gap-6">
            <div className="flex items-start gap-4 shrink-0">
              <div className="relative">
                <div className="h-20 w-20 rounded-full bg-slate-200 flex items-center justify-center text-xl font-semibold text-slate-600 overflow-hidden">
                  {avatarPreview || profile?.avatar ? (
                    <img src={avatarPreview || profile.avatar} alt={getDisplayName()} className="h-full w-full object-cover" />
                  ) : (
                    getInitials()
                  )}
                </div>
                {editing && (
                  <>
                    <input type="file" id="avatar-upload" accept="image/*" onChange={handleAvatarChange} className="sr-only" />
                    <label htmlFor="avatar-upload" className="absolute bottom-0 right-0 p-1.5 rounded-full bg-slate-900 text-white cursor-pointer hover:bg-slate-800">
                      <FiCamera className="h-4 w-4" />
                    </label>
                  </>
                )}
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-semibold text-slate-900 truncate">{getDisplayName()}</h2>
                <div className="flex flex-wrap items-center gap-2 mt-1">
                  <span className="inline-flex px-2.5 py-1 rounded-md text-sm font-medium bg-slate-900 text-white">{profile?.role}</span>
                  {profile?.grade && <span className="inline-flex px-2.5 py-1 rounded-md text-sm font-medium bg-slate-100 text-slate-700 border border-slate-200">{profile.grade}</span>}
                  <span className="text-sm text-slate-500">Joined {new Date(profile?.createdAt || profile?.joinedDate).toLocaleDateString()}</span>
                </div>
                <p className="text-sm text-slate-600 mt-2">{profile?.bio || 'No bio'}</p>
                {profile?.stats && (
                  <div className="mt-3">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Weekly goal</span>
                      <span>{profile.stats.completedThisWeek}/{profile.stats.weeklyGoal} tests</span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-slate-600 transition-all" style={{ width: `${getProgressPercentage()}%` }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-2 sm:ml-auto shrink-0">
              {!editing ? (
                <button type="button" onClick={() => setEditing(true)} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
                  <FiEdit3 className="h-4 w-4" /> Edit Profile
                </button>
              ) : (
                <>
                  <button type="button" onClick={handleSaveProfile} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60 transition-colors shadow-sm">
                    <FiSave className="h-4 w-4" /> {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button type="button" onClick={() => { setEditing(false); setFormData({ fullName: profile?.fullName || '', email: profile?.email || '', phone: profile?.phone || '', dateOfBirth: profile?.dateOfBirth ? profile.dateOfBirth.split('T')[0] : '', address: profile?.address || '', bio: profile?.bio || '' }); setAvatarFile(null); setAvatarPreview(null); }} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
                    <FiX className="h-4 w-4" /> Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {(profile?.stats || profile?.role) && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
            {[
              { Icon: FiBookOpen, value: profile?.stats?.testsCompleted ?? 0, label: 'Tests' },
              { Icon: FiTrendingUp, value: `${profile?.stats?.averageScore ?? 0}%`, label: 'Avg Score' },
              { Icon: FiClock, value: `${profile?.stats?.studyHours ?? 0}h`, label: 'Study' },
              { Icon: FiTarget, value: `#${profile?.stats?.rank ?? '—'}`, label: 'Rank' },
              { Icon: FiAward, value: Array.isArray(profile?.stats?.achievements) ? profile.stats.achievements.length : (profile?.stats?.achievements ?? effectiveAchievements.length), label: 'Achievements' },
              { Icon: FiStar, value: profile?.stats?.totalPoints ?? 0, label: 'Points' }
            ].map(({ Icon, value, label }) => (
              <div key={label} className="rounded-lg border border-slate-200 bg-white shadow-sm p-3">
                <Icon className="h-4 w-4 text-slate-400 mb-1" />
                <div className="text-lg font-semibold text-slate-900">{value}</div>
                <div className="text-xs text-slate-500">{label}</div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 mb-4">
          {tabBtn('personal', <FiUser className="h-4 w-4" />, 'Personal')}
          {tabBtn('security', <FiShield className="h-4 w-4" />, 'Security')}
          {tabBtn('preferences', <FiSettings className="h-4 w-4" />, 'Preferences')}
          {tabBtn('achievements', <FiAward className="h-4 w-4" />, 'Achievements')}
          {tabBtn('referrals', <FiGift className="h-4 w-4" />, 'Referrals')}
          {tabBtn('activity', <FiActivity className="h-4 w-4" />, 'Activity')}
          {profile?.role === 'teacher' && tabBtn('teaching', <FiBookOpen className="h-4 w-4" />, 'Teaching')}
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-6">
            {activeTab === 'personal' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Personal Information</h3>
                <p className="text-sm text-slate-500 mb-4">Manage your details and contact information.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelClass}>Full name</label>
                    <input type="text" value={formData.fullName || ''} onChange={(e) => handleInputChange('fullName', e.target.value)} disabled={!editing} className={inputClass} placeholder="Full name" />
                  </div>
                  <div>
                    <label className={labelClass}>Email</label>
                    <input type="email" value={formData.email || ''} onChange={(e) => handleInputChange('email', e.target.value)} disabled={!editing} className={inputClass} placeholder="Email" />
                  </div>
                  <div>
                    <label className={labelClass}>Phone</label>
                    <input type="tel" value={formData.phone || ''} onChange={(e) => handleInputChange('phone', e.target.value)} disabled={!editing} className={inputClass} placeholder="Phone" />
                  </div>
                  <div>
                    <label className={labelClass}>Date of birth</label>
                    <input type="date" value={formData.dateOfBirth || ''} onChange={(e) => handleInputChange('dateOfBirth', e.target.value)} disabled={!editing} className={inputClass} />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Address</label>
                    <input type="text" value={formData.address || ''} onChange={(e) => handleInputChange('address', e.target.value)} disabled={!editing} className={inputClass} placeholder="Address" />
                  </div>
                  <div className="sm:col-span-2">
                    <label className={labelClass}>Bio</label>
                    <textarea value={formData.bio || ''} onChange={(e) => handleInputChange('bio', e.target.value)} disabled={!editing} className={`${inputClass} resize-y`} rows={3} placeholder="About you" />
                  </div>
                  {profile?.role === 'parent' && profile?.children?.length > 0 && (
                    <div className="sm:col-span-2">
                      <label className={labelClass}><FiUsers className="h-4 w-4 inline mr-1" /> Linked children</label>
                      <ul className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                        {profile.children.map((child) => (
                          <li key={child._id} className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="font-medium text-slate-900">{child.fullName || child.email}</span>
                            {child.grade && <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium border border-slate-200 bg-white text-slate-700">{child.grade}</span>}
                            {child.email && <span className="text-slate-500">{child.email}</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
            {activeTab === 'teaching' && profile?.role === 'teacher' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Teaching Settings</h3>
                <p className="text-sm text-slate-500 mb-4">Subjects and rates.</p>
                <div className="space-y-4">
                  <div>
                    <label className={labelClass}>Subjects offered</label>
                    <div className="flex flex-wrap gap-3">
                      {subjectOptions.map((subject) => (
                        <label key={subject} className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={(teaching.subjects || []).includes(subject)}
                            onChange={(e) => toggleSubject(subject, e.target.checked)}
                            disabled={!editing}
                            className="rounded border-slate-300 text-slate-900 focus:ring-slate-400"
                          />
                          <span>{subject}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className={labelClass}>Rates (₦/hr)</label>
                    {(teaching.subjects || []).length === 0 ? (
                      <p className="text-sm text-slate-500">Select subjects to set rates.</p>
                    ) : (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(teaching.subjects || []).map((subject) => (
                          <div key={subject}>
                            <label className={labelClass}>{subject}</label>
                            <input type="number" min={0} step={50} className={inputClass} value={(teaching.rates || {})[subject] || ''} onChange={(e) => setTeaching(prev => ({ ...prev, rates: { ...(prev.rates || {}), [subject]: Number(e.target.value) } }))} disabled={!editing} placeholder="Rate" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <button type="button" onClick={handleSaveTeaching} disabled={saving} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60 transition-colors shadow-sm">
                    <FiSave className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Teaching'}
                  </button>
                </div>
              </div>
            )}
            
            {activeTab === 'security' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Security</h3>
                <p className="text-sm text-slate-500 mb-4">Password and account actions.</p>
                <div className="space-y-4">
                  <h4 className="text-sm font-medium text-slate-700">Change password</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className={labelClass}>Current password</label>
                      <div className="relative">
                        <input type={showPasswords.current ? 'text' : 'password'} value={passwordData.currentPassword} onChange={(e) => handlePasswordChange('currentPassword', e.target.value)} className={inputClass} placeholder="Current password" />
                        <button type="button" onClick={() => togglePasswordVisibility('current')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600">{showPasswords.current ? <FiEyeOff size={16} /> : <FiEye size={16} />}</button>
                      </div>
                    </div>
                    <div>
                      <label className={labelClass}>New password</label>
                      <div className="relative">
                        <input type={showPasswords.new ? 'text' : 'password'} value={passwordData.newPassword} onChange={(e) => handlePasswordChange('newPassword', e.target.value)} className={inputClass} placeholder="New password" />
                        <button type="button" onClick={() => togglePasswordVisibility('new')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600">{showPasswords.new ? <FiEyeOff size={16} /> : <FiEye size={16} />}</button>
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelClass}>Confirm new password</label>
                      <div className="relative">
                        <input type={showPasswords.confirm ? 'text' : 'password'} value={passwordData.confirmPassword} onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)} className={inputClass} placeholder="Confirm" />
                        <button type="button" onClick={() => togglePasswordVisibility('confirm')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600">{showPasswords.confirm ? <FiEyeOff size={16} /> : <FiEye size={16} />}</button>
                      </div>
                    </div>
                  </div>
                  
                  <button
                    type="button"
                    onClick={handleChangePassword}
                    disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-50 disabled:pointer-events-none transition-colors shadow-sm"
                  >
                    <FiSave className="h-4 w-4" /> Change Password
                  </button>
                </div>

                <div className="mt-6 pt-4 border-t border-slate-200">
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Account actions</h4>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" onClick={handleExportData} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
                      <FiDownload className="h-4 w-4" /> Export My Data
                    </button>
                    <button type="button" onClick={handleDeleteAccount} className="inline-flex items-center gap-2 px-4 py-2.5 border border-red-200 bg-white text-red-600 text-sm font-medium rounded-lg hover:bg-red-50 transition-colors">
                      <FiTrash2 className="h-4 w-4" /> Delete Account
                    </button>
                  </div>
                </div>
              </div>
            )}

            {activeTab === 'preferences' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Preferences</h3>
                <p className="text-sm text-slate-500 mb-4">Notifications and privacy.</p>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Notifications</h4>
                    <div className="space-y-3">
                      {[
                        { key: 'email', Icon: FiBell, label: 'Email', desc: 'Updates via email' },
                        { key: 'push', Icon: FiBell, label: 'Push', desc: 'Push notifications' },
                        { key: 'testReminders', Icon: FiAlertCircle, label: 'Test reminders', desc: 'Upcoming tests' }
                      ].map(({ key, Icon, label, desc }) => (
                        <div key={key} className="flex items-center justify-between py-2 border-b border-slate-100">
                          <div className="flex items-center gap-3">
                            <Icon className="h-4 w-4 text-slate-400" />
                            <div>
                              <span className="text-sm font-medium text-slate-700">{label}</span>
                              <p className="text-xs text-slate-500">{desc}</p>
                            </div>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" checked={preferences.notifications[key]} onChange={(e) => handlePreferenceChange('notifications', key, e.target.checked)} className="sr-only peer" />
                            <div className="relative w-9 h-5 shrink-0 rounded-full bg-slate-200 peer-checked:bg-slate-900 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-4" />
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Privacy</h4>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <FiEye className="h-4 w-4 text-slate-400" />
                        <div>
                          <span className="text-sm font-medium text-slate-700">Profile visibility</span>
                          <p className="text-xs text-slate-500">Who can see your profile</p>
                        </div>
                      </div>
                      <select value={preferences.privacy.profileVisibility} onChange={(e) => handlePreferenceChange('privacy', 'profileVisibility', e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30">
                        <option value="public">Public</option>
                        <option value="friends">Friends only</option>
                        <option value="private">Private</option>
                      </select>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <FiTrendingUp className="h-4 w-4 text-slate-400" />
                        <div>
                          <span className="text-sm font-medium text-slate-700">Show progress</span>
                          <p className="text-xs text-slate-500">Display learning progress</p>
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input type="checkbox" checked={preferences.privacy.showProgress} onChange={(e) => handlePreferenceChange('privacy', 'showProgress', e.target.checked)} className="sr-only peer" />
                        <div className="relative w-9 h-5 shrink-0 rounded-full bg-slate-200 peer-checked:bg-slate-900 after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-transform peer-checked:after:translate-x-4" />
                      </label>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Display</h4>
                    <div className="flex items-center justify-between py-2 border-b border-slate-100">
                      <div className="flex items-center gap-3">
                        <FiGlobe className="h-4 w-4 text-slate-400" />
                        <div>
                          <span className="text-sm font-medium text-slate-700">Language</span>
                          <p className="text-xs text-slate-500">Preferred language</p>
                        </div>
                      </div>
                      <select value={preferences.display.language} onChange={(e) => handlePreferenceChange('display', 'language', e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30">
                        <option value="en">English</option>
                        <option value="fr">French</option>
                        <option value="es">Spanish</option>
                      </select>
                    </div>
                  </div>
                </div>
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <button type="button" onClick={handleSavePreferences} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors shadow-sm">
                    <FiSave className="h-4 w-4" /> Save Preferences
                  </button>
                </div>
              </div>
            )}

            {activeTab === 'referrals' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Referrals</h3>
                <p className="text-sm text-slate-500 mb-4">Invite friends and earn rewards.</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                  {[
                    { Icon: FiUsers, value: referralStats.totalReferrals ?? 0, label: 'Referrals' },
                    { Icon: FiDollarSign, value: formatCurrency(referralStats.totalEarnings ?? 0), label: 'Earnings' },
                    { Icon: FiGift, value: referralStats.pendingRewards ?? 0, label: 'Pending' },
                    { Icon: FiTrendingUp, value: formatCurrency(referralStats.subscriptionCommissions?.totalAmount ?? 0), label: 'Commissions' }
                  ].map(({ Icon, value, label }) => (
                    <div key={label} className="rounded-lg border border-slate-200 bg-slate-50/50 p-3">
                      <Icon className="h-4 w-4 text-slate-400 mb-1" />
                      <div className="text-lg font-semibold text-slate-900">{value}</div>
                      <div className="text-xs text-slate-500">{label}</div>
                    </div>
                  ))}
                </div>
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-3">Share & earn</h4>
                  {referralLoading ? (
                    <div className="flex items-center gap-2 py-6 text-slate-500">
                      <LoadingSpinner size="small" />
                      <span>Loading…</span>
                    </div>
                  ) : referralError ? (
                    <div className="flex items-center gap-2 py-4 px-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
                      <FiAlertCircle size={20} />
                      <span>{referralError}</span>
                    </div>
                  ) : !referralCode ? (
                    <div className="text-center py-8 px-4 rounded-lg border border-slate-200 bg-slate-50/50">
                      <FiGift className="h-10 w-10 text-slate-400 mx-auto mb-2" />
                      <p className="text-sm text-slate-600 mb-4">Generate your code to get a shareable link and QR code.</p>
                      <button type="button" onClick={handleGenerateReferralCode} disabled={generatingReferral} className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 disabled:opacity-60 transition-colors shadow-sm">
                        <FiGift className="h-4 w-4" /> {generatingReferral ? 'Generating…' : 'Generate referral code'}
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className={labelClass}>Referral code</label>
                        <div className="flex items-center gap-2">
                          <code className="flex-1 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-sm font-mono">{referralCode}</code>
                          <button type="button" onClick={() => copyToClipboard(referralCode, 'Referral code')} className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
                            <FiCopy className="h-3.5 w-3.5" /> Copy
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className={labelClass}>Referral link</label>
                        <div className="flex items-center gap-2">
                          <input type="text" readOnly value={referralUrl || ''} className={`${inputClass} flex-1`} />
                          <button type="button" onClick={() => copyToClipboard(referralUrl, 'Referral link')} disabled={!referralUrl} className="inline-flex items-center gap-1.5 px-3 py-2 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:pointer-events-none transition-colors">
                            <FiLink className="h-3.5 w-3.5" /> Copy
                          </button>
                        </div>
                      </div>
                      <div className="sm:col-span-2">
                        <label className={labelClass}>QR code</label>
                        {referralQR ? (
                          <div className="inline-block p-3 rounded-lg border border-slate-200 bg-white">
                            <img src={referralQR} alt="Referral QR" className="w-32 h-32" />
                            <div className="flex gap-2 mt-2">
                              <button type="button" onClick={downloadQRCode} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors">
                                <FiDownload className="h-3.5 w-3.5" /> Download
                              </button>
                              <button type="button" onClick={() => copyToClipboard(referralUrl, 'Referral link')} disabled={!referralUrl} className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 disabled:opacity-50 transition-colors">
                                <FiCopy className="h-3.5 w-3.5" /> Copy link
                              </button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-slate-500">QR available after code is generated.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'achievements' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Achievements</h3>
                <p className="text-sm text-slate-500 mb-4">Your milestones.</p>
                {effectiveAchievements.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {effectiveAchievements.map((achievement, index) => (
                      <div key={index} className="rounded-lg border border-slate-200 bg-slate-50/50 p-4 flex gap-3">
                        <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                          <FiAward className="h-5 w-5 text-amber-700" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="font-medium text-slate-900">{achievement.title}</h4>
                          <p className="text-sm text-slate-600 mt-0.5">{achievement.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            {(() => {
                              const v = getAchievementBadgeColor(achievement.rarity);
                              const classes = { primary: 'bg-slate-900 text-white', secondary: 'bg-slate-100 text-slate-600', warning: 'bg-amber-100 text-amber-800', success: 'bg-emerald-100 text-emerald-800' };
                              return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${classes[v] || classes.primary}`}>{achievement.rarity || 'achievement'}</span>;
                            })()}
                            {achievement.earnedAt && <span className="text-xs text-slate-500">{new Date(achievement.earnedAt).toLocaleDateString()}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12 rounded-lg border border-slate-200 bg-slate-50/50">
                    <FiAward className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <h4 className="text-sm font-medium text-slate-700">No achievements yet</h4>
                    <p>Complete tests and reach milestones to earn achievements.</p>
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'activity' && (
              <div>
                <h3 className="text-sm font-medium text-slate-900 border-b border-slate-200 pb-2 mb-4">Activity</h3>
                <p className="text-sm text-slate-500 mb-4">Test completions and progress.</p>
                {effectiveActivity.length > 0 ? (
                  <ul className="space-y-3">
                    {effectiveActivity.map((activity, index) => (
                      <li key={index} className="flex gap-3 py-3 border-b border-slate-100 last:border-0">
                        <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                          <FiActivity className="h-4 w-4 text-slate-500" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <h4 className="text-sm font-medium text-slate-900">{activity.title}</h4>
                          {activity.description && <p className="text-sm text-slate-600">{activity.description}</p>}
                          {activity.timestamp && <span className="text-xs text-slate-500">{new Date(activity.timestamp).toLocaleString()}</span>}
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="text-center py-12 rounded-lg border border-slate-200 bg-slate-50/50">
                    <FiActivity className="h-12 w-12 text-slate-300 mx-auto mb-2" />
                    <h4 className="text-sm font-medium text-slate-700">No recent activity</h4>
                    <p className="text-sm text-slate-500 mt-1">Complete tests to see activity here.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
  );
};

export default Profile;